import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Lock, Mail, Phone, Sparkles, ShieldCheck, ArrowRight, KeyRound,
  PlayCircle, Building2, Briefcase, Home, Wrench, TrendingUp, Crown,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/utils/api';
import { getPortal, PORTAL_KEYS } from '@/config/portals';
import RevaLogo from '@/components/RevaLogo';

const ICONS = {
  'platform-owner': Crown,
  manager: Briefcase,
  'investment-manager': TrendingUp,
  resident: Home,
  owner: Crown,
  vendor: Wrench,
  investor: Building2,
};

// ─────────────────────────────────────────────────────────────────────────────
// Universal portal login.
// Route: /login/:portal  (portal ∈ manager · investment-manager · resident · owner · vendor · investor)
// Adapts visual treatment + available auth methods from portals.js
// ─────────────────────────────────────────────────────────────────────────────
export default function PortalLogin() {
  const { portal: portalSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const portal = useMemo(() => getPortal(portalSlug), [portalSlug]);
  const Icon = ICONS[portal.slug] || Briefcase;

  // Magic-link auto-redeem if ?token=... is present in URL
  useEffect(() => {
    const tokenInUrl = searchParams.get('token');
    if (!tokenInUrl) return;
    (async () => {
      try {
        const { data } = await api.redeemAccessLink(tokenInUrl);
        localStorage.setItem('token', data.access_token);
        toast.success('Signed in via secure link');
        navigate(data.redirect || portal.afterLogin, { replace: true });
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Invalid or expired link');
      }
    })();
    // eslint-disable-next-line
  }, [searchParams]);

  // Default tab depends on portal preferences
  const defaultMethod = portal.auth.otp && !portal.auth.password
    ? 'otp'
    : (portal.auth.preferMagic ? 'magic' : 'password');
  const [method, setMethod] = useState(defaultMethod);
  const [busy, setBusy] = useState(false);

  // Password fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Magic-link
  const [magicEmail, setMagicEmail] = useState('');
  const [magicSent, setMagicSent] = useState(null);

  // OTP
  const [phone, setPhone] = useState('+966');
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpDebug, setOtpDebug] = useState(null);
  const [otpCode, setOtpCode] = useState('');

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      // After login, hit /api/auth/portal-redirect to get the right route
      const { data } = await api.portalRedirect();
      toast.success('Welcome back');
      navigate(data.redirect || portal.afterLogin, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid credentials');
    } finally { setBusy(false); }
  };

  const submitMagic = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.requestAccessLink({ email: magicEmail, portal: portal.slug });
      setMagicSent(data.debug_link || '__sent__');
      toast.success('Check your inbox for the secure access link.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send link');
    } finally { setBusy(false); }
  };

  const submitOtpRequest = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.requestOtp({ phone, portal: portal.slug });
      setOtpRequested(true);
      setOtpDebug(data.debug_code || null);
      toast.success('Code sent to your phone.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send code');
    } finally { setBusy(false); }
  };

  const submitOtpVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.verifyOtp({ phone, code: otpCode });
      localStorage.setItem('token', data.access_token);
      toast.success('Signed in');
      navigate(data.redirect || portal.afterLogin, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid code');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" style={{ background: portal.bg }} data-testid={`portal-login-${portal.slug}`}>
      {/* ─── Left brand panel ────────────────────────────────────────── */}
      <aside
        className="md:w-[44%] xl:w-[40%] text-white p-8 md:p-12 lg:p-16 flex flex-col justify-between relative overflow-hidden"
        style={{ background: portal.panel, color: portal.panelTextOn }}
      >
        {/* Decorative grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
            backgroundSize: '36px 36px',
          }}
        />
        <div className="relative z-10">
          <Link to="/" className="inline-flex items-center gap-2.5" data-testid="portal-logo">
            <RevaLogo color="white" />
          </Link>
          <div
            className="inline-flex items-center gap-2 px-2.5 py-1 mt-10 rounded-full text-[10.5px] font-bold tracking-[0.18em] reva-mono uppercase"
            style={{ background: `${portal.accent}22`, color: portal.accent, border: `1px solid ${portal.accent}55` }}
          >
            <Icon className="h-3 w-3" /> {portal.label_en}
          </div>
          <h1 className="mt-6 text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            {portal.label_en}
          </h1>
          <p className="mt-3 text-[15px] max-w-md opacity-80 leading-relaxed">{portal.tagline_en}</p>
        </div>

        {/* Owner portal — embedded onboarding video card */}
        {portal.slug === 'owner' && (
          <div className="relative z-10 rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-sm p-5 max-w-md" data-testid="owner-portal-video-card">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                <PlayCircle className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[12px] uppercase tracking-[0.18em] opacity-70 reva-mono">Onboarding</div>
                <div className="mt-1 text-[15px] font-semibold">Welcome to your Owner Portal</div>
                <p className="mt-1 text-[12.5px] opacity-75">A 90-second walkthrough of your executive dashboard, cash-flow view, and AI portfolio insights.</p>
              </div>
            </div>
          </div>
        )}

        {/* Vendor — quick stat callout */}
        {portal.slug === 'vendor' && (
          <div className="relative z-10 rounded-2xl bg-white/[0.05] border border-white/10 backdrop-blur-sm p-5 max-w-md" data-testid="vendor-mobile-callout">
            <div className="flex items-start gap-3">
              <Phone className="h-5 w-5 mt-0.5 shrink-0" />
              <div>
                <div className="text-[12px] uppercase tracking-[0.18em] opacity-70 reva-mono">Mobile-first</div>
                <div className="mt-1 text-[15px] font-semibold">Sign in with your phone — no email needed</div>
                <p className="mt-1 text-[12.5px] opacity-75">Receive a 6-digit code via SMS or WhatsApp. Built for the way Saudi vendors work in the field.</p>
              </div>
            </div>
          </div>
        )}

        {/* Investor — capital partner copy */}
        {portal.slug === 'investor' && (
          <div className="relative z-10 rounded-2xl bg-white/[0.05] border border-white/10 backdrop-blur-sm p-5 max-w-md" data-testid="investor-callout">
            <div className="flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 mt-0.5 shrink-0" />
              <div>
                <div className="text-[12px] uppercase tracking-[0.18em] opacity-70 reva-mono">Institutional</div>
                <div className="mt-1 text-[15px] font-semibold">Passwordless secure access</div>
                <p className="mt-1 text-[12.5px] opacity-75">We send a one-time access link to your verified email — no passwords to lose, audit-trail by default.</p>
              </div>
            </div>
          </div>
        )}

        {/* Manager — operations callout */}
        {portal.slug === 'manager' && (
          <div className="relative z-10 rounded-2xl bg-white/[0.05] border border-white/10 backdrop-blur-sm p-5 max-w-md" data-testid="manager-callout">
            <div className="text-[12px] uppercase tracking-[0.18em] opacity-70 reva-mono">Operational</div>
            <div className="mt-1 text-[15px] font-semibold">Run every property, lease, and ticket from one screen.</div>
            <p className="mt-1 text-[12.5px] opacity-75">Built for property managers handling 10 to 10,000 units — from a single building to a national portfolio.</p>
          </div>
        )}

        {/* Investment Manager — capital callout */}
        {portal.slug === 'investment-manager' && (
          <div className="relative z-10 rounded-2xl bg-white/[0.05] border border-white/10 backdrop-blur-sm p-5 max-w-md" data-testid="investment-manager-callout">
            <div className="text-[12px] uppercase tracking-[0.18em] opacity-70 reva-mono">Institutional</div>
            <div className="mt-1 text-[15px] font-semibold">Fund-level reporting · capital performance</div>
            <p className="mt-1 text-[12.5px] opacity-75">For asset managers and investment firms — committed/deployed capital, NOI roll-ups, distribution history, and AI capital forecasting.</p>
          </div>
        )}

        {/* Resident — friendly callout */}
        {portal.slug === 'resident' && (
          <div className="relative z-10 rounded-2xl bg-white/[0.05] border border-white/10 backdrop-blur-sm p-5 max-w-md" data-testid="resident-callout">
            <div className="text-[12px] uppercase tracking-[0.18em] opacity-70 reva-mono">Mobile-friendly</div>
            <div className="mt-1 text-[15px] font-semibold">Pay rent · raise a ticket · access your manuals</div>
            <p className="mt-1 text-[12.5px] opacity-75">Designed for residents in KSA — Arabic-first, WhatsApp-friendly, works perfectly on a phone.</p>
          </div>
        )}

        {/* Platform Owner — sovereign callout */}
        {portal.slug === 'platform-owner' && (
          <div className="relative z-10 rounded-2xl bg-white/[0.05] border border-white/10 backdrop-blur-sm p-5 max-w-md" data-testid="platform-owner-callout">
            <div className="text-[12px] uppercase tracking-[0.18em] opacity-70 reva-mono">Sovereign access</div>
            <div className="mt-1 text-[15px] font-semibold">Global platform control</div>
            <p className="mt-1 text-[12.5px] opacity-75">Sees every organization, every subscription, every audit event. Suspend, activate, migrate. Password-only by design.</p>
          </div>
        )}

        {!['owner', 'vendor', 'investor', 'manager', 'investment-manager', 'resident', 'platform-owner'].includes(portal.slug) && (
          <div className="relative z-10 rounded-2xl bg-white/[0.05] border border-white/10 backdrop-blur-sm p-5 max-w-md">
            <div className="text-[12px] uppercase tracking-[0.18em] opacity-70 reva-mono">Why REVA</div>
            <div className="mt-2 text-[13.5px] opacity-90 leading-relaxed">
              Performance method · governance built-in · Saudi-native pricing — engineered for institutional operators.
            </div>
          </div>
        )}
      </aside>

      {/* ─── Right form panel ────────────────────────────────────────── */}
      <main className="flex-1 flex items-start md:items-center justify-center px-6 py-10 md:p-12">
        <div className="w-full max-w-[440px]">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="text-[12px] text-[#4A5263] hover:text-[#0E1320]" data-testid="portal-back-home">← Back to REVA</Link>
            <div className="text-[11px] reva-mono uppercase tracking-[0.18em] text-[#71777E]">{portal.badge_en}</div>
          </div>

          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#0E1320]">Sign in</h2>
          <p className="mt-1.5 text-[13.5px] text-[#4A5263]">
            Use your invitation credentials. REVA is invitation-only — accounts cannot self-register.
          </p>

          {/* Method selector */}
          <div className="flex gap-1 mt-6 mb-5" role="tablist">
            {portal.auth.password && (
              <MethodTab active={method === 'password'} onClick={() => setMethod('password')} icon={Lock} label="Password" testid="method-password" accent={portal.accent} />
            )}
            {portal.auth.magicLink && (
              <MethodTab active={method === 'magic'} onClick={() => setMethod('magic')} icon={Sparkles} label="Access link" testid="method-magic" accent={portal.accent} />
            )}
            {portal.auth.otp && (
              <MethodTab active={method === 'otp'} onClick={() => setMethod('otp')} icon={Phone} label="Phone OTP" testid="method-otp" accent={portal.accent} />
            )}
          </div>

          {/* PASSWORD form */}
          {method === 'password' && portal.auth.password && (
            <form onSubmit={submitPassword} className="space-y-3" data-testid="form-password">
              <Field icon={Mail} label="Email" type="email" value={email} onChange={setEmail} required testid="login-email" />
              <Field icon={Lock} label="Password" type="password" value={password} onChange={setPassword} required testid="login-password" />
              <button disabled={busy} type="submit" data-testid="login-submit"
                className="w-full mt-2 inline-flex items-center justify-center gap-2 h-11 rounded-xl font-semibold text-white text-[14px] disabled:opacity-60 transition-transform active:scale-[0.99]"
                style={{ background: portal.accent }}>
                {busy ? 'Signing in…' : 'Sign in'} <ArrowRight className="h-4 w-4" />
              </button>
              <div className="flex items-center justify-between text-[12px] mt-3">
                <button type="button" onClick={() => setMethod('magic')} className="text-[#4A5263] hover:text-[#0E1320] underline-offset-2 hover:underline">
                  Forgot password — email me a link
                </button>
              </div>
            </form>
          )}

          {/* MAGIC LINK form */}
          {method === 'magic' && portal.auth.magicLink && (
            <form onSubmit={submitMagic} className="space-y-3" data-testid="form-magic">
              <Field icon={Mail} label="Work email" type="email" value={magicEmail} onChange={setMagicEmail} required testid="magic-email" />
              <button disabled={busy} type="submit" data-testid="magic-submit"
                className="w-full mt-2 inline-flex items-center justify-center gap-2 h-11 rounded-xl font-semibold text-white text-[14px] disabled:opacity-60"
                style={{ background: portal.accent }}>
                {busy ? 'Sending…' : 'Send secure access link'} <Sparkles className="h-4 w-4" />
              </button>
              {magicSent && (
                <div className="mt-3 p-3 rounded-xl border bg-white text-[12.5px] text-[#0E1320]" data-testid="magic-sent">
                  <div className="font-medium">Access link sent</div>
                  <p className="mt-1 text-[#4A5263]">If the email is registered you will receive a secure link valid for 30 minutes.</p>
                  {magicSent !== '__sent__' && (
                    <div className="mt-2 reva-mono text-[11px] text-[#71777E] break-all border-t border-[#F1F4F7] pt-2">
                      <span className="text-amber-700">Dev preview:</span>{' '}
                      <Link to={magicSent} className="underline">{magicSent}</Link>
                    </div>
                  )}
                </div>
              )}
            </form>
          )}

          {/* OTP form */}
          {method === 'otp' && portal.auth.otp && (
            <div data-testid="form-otp">
              {!otpRequested ? (
                <form onSubmit={submitOtpRequest} className="space-y-3">
                  <Field icon={Phone} label="Mobile number" type="tel" value={phone} onChange={setPhone} placeholder="+9665XXXXXXXX" required testid="otp-phone" />
                  <button disabled={busy} type="submit" data-testid="otp-request"
                    className="w-full mt-2 inline-flex items-center justify-center gap-2 h-11 rounded-xl font-semibold text-white text-[14px] disabled:opacity-60"
                    style={{ background: portal.accent }}>
                    {busy ? 'Sending…' : 'Send 6-digit code'} <KeyRound className="h-4 w-4" />
                  </button>
                  <p className="mt-2 text-[11.5px] text-[#71777E] leading-relaxed">
                    Code arrives via SMS (and WhatsApp for vendors once integration is live). No password to remember.
                  </p>
                </form>
              ) : (
                <form onSubmit={submitOtpVerify} className="space-y-3">
                  <div className="text-[12.5px] text-[#4A5263]">Enter the 6-digit code sent to <span className="reva-mono text-[#0E1320]">{phone}</span></div>
                  <Field icon={KeyRound} label="6-digit code" type="text" value={otpCode} onChange={(v) => setOtpCode(v.replace(/\D/g, '').slice(0, 6))} required testid="otp-code" />
                  {otpDebug && (
                    <div className="text-[11px] reva-mono text-amber-700">Dev preview · code: <span className="font-bold">{otpDebug}</span></div>
                  )}
                  <button disabled={busy || otpCode.length !== 6} type="submit" data-testid="otp-verify"
                    className="w-full mt-2 inline-flex items-center justify-center gap-2 h-11 rounded-xl font-semibold text-white text-[14px] disabled:opacity-60"
                    style={{ background: portal.accent }}>
                    {busy ? 'Verifying…' : 'Verify & sign in'} <ArrowRight className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={() => { setOtpRequested(false); setOtpDebug(null); }} className="text-[12px] text-[#4A5263] underline-offset-2 hover:underline">
                    Send a new code
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Portal switcher */}
          <div className="mt-10 pt-6 border-t border-[#E6EAF0]">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#71777E] reva-mono">Other portals</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {PORTAL_KEYS.filter(s => s !== portal.slug).map(s => {
                const p = getPortal(s); const PI = ICONS[s] || Briefcase;
                return (
                  <Link key={s} to={`/login/${s}`} data-testid={`portal-switch-${s}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F1F4F7] hover:bg-[#E6EAF0] text-[11.5px] text-[#0E1320] transition-colors">
                    <PI className="h-3 w-3" /> {p.label_en}
                  </Link>
                );
              })}
            </div>
          </div>

          <p className="mt-8 text-[11.5px] text-[#71777E] leading-relaxed">
            By signing in you agree to REVA's <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy</Link>.
            REVA is an invitation-only platform — to request an account, <Link to="/" className="underline">contact our team</Link>.
          </p>
        </div>
      </main>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────
function MethodTab({ active, onClick, icon: Icon, label, testid, accent }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium transition-all ${
        active ? 'text-white' : 'bg-[#F1F4F7] text-[#0E1320] hover:bg-[#E6EAF0]'
      }`}
      style={active ? { background: accent } : {}}>
      <Icon className="h-3 w-3" /> {label}
    </button>
  );
}

function Field({ icon: Icon, label, type, value, onChange, placeholder, required, testid }) {
  return (
    <label className="block">
      <span className="text-[12px] text-[#4A5263]">{label}</span>
      <div className="mt-1 relative">
        <Icon className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" strokeWidth={1.7} />
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder}
          data-testid={testid}
          className="w-full h-11 pl-10 pe-3 rounded-xl border border-[#E6EAF0] bg-white text-[13.5px] text-[#0E1320] placeholder-[#9CA3AF] focus:outline-none focus:border-[#0E1320] focus:ring-2 focus:ring-[#0E132011]" />
      </div>
    </label>
  );
}
